<template>
  <div>
    <h1 class="flex mb-8 font-bold text-3xl group">
      <inertia-link class="flex text-indigo-400 group-hover:text-indigo-600" :href="route('indicators.register')">
        <icon name="passport" class="w-6 h-9 mr-3 fill-indigo-400 group-hover:fill-indigo-600" /> Indicadores /
      </inertia-link>
      <span class="ml-1 font-medium">
        {{ indicator.label }}
      </span>
    </h1>

    <div class="bg-white rounded-md shadow overflow-hidden w-full">
      <form @submit.prevent="update">
        <div class="p-8 -mr-6 -mb-8 flex flex-wrap">
          <select-input
            v-model="form.organization"
            :error="form.errors.organization"
            class="pr-6 pb-8 w-full lg:w-1/2"
            :class="{'hidden': false}"
            label="Organização"
          >
            <option :value="null">...</option>
            <option
              v-for="organization in organizations"
              :key="organization.id"
              :value="organization.id"
            >
              {{ organization.name }}
            </option>
          </select-input>

          <select-input
            v-model="form.role"
            :error="form.errors.role"
            class="pr-6 pb-8 w-full lg:w-1/2"
            :class="{'hidden': false}"
            label="Papel"
          >
            <option :value="null">...</option>
            <option
              v-for="role in roles"
              :key="role.id"
              :value="role.id"
            >
              {{ role.name }}
            </option>
          </select-input>

          <text-input
            v-model="form.label"
            :error="form.errors.label"
            class="pr-6 pb-8 w-full lg:w-1/2"
            label="Título"
          />

          <select-input
            v-model="form.active"
            :error="form.errors.active"
            class="pr-6 pb-8 w-full lg:w-1/2"
            label="Vigente"
          >
            <option :value="true">Sim</option>
            <option :value="false">Não</option>
          </select-input>

          <div class="pr-6 pb-8 w-full lg:w-1/2">
            <label for="state" class="form-label">Estado:</label>

            <select
              id="state"
              v-model="form.state"
              class="form-control form-select"
              @change="getCities()"
            >
              <option :value="null" />
              <option v-for="state in states" :key="state.id" :value="state.id" :selected="form.state == state.id">{{ state.name }}</option>
            </select>
          </div>

          <div class="pr-6 pb-8 w-full lg:w-1/2">
            <label for="city" class="form-label">Cidade:</label>

            <select id="city" v-model="form.city" class="form-control form-select">
              <option :value="null" />
              <option v-for="city in userCities" :key="city.id" :value="city.id" :selected="form.city == city.id">{{ city.name }}</option>
            </select>
          </div>
        </div>

        <table class="w-full whitespace-nowrap">
          <caption class="text-left ml-8">Valores:</caption>
          <tr class="text-left font-bold">
            <th class="px-6 pt-6 pb-4">Tipo</th>
            <th class="px-6 pt-6 pb-4">Início de Vigência</th>
            <th class="px-6 pt-6 pb-4">Final de Vigência </th>
            <th class="px-6 pt-6 pb-4">Valor</th>
          </tr>

          <tr v-for="(value, index) in form.values" :key="value.id" :class="{'hidden': value.hidden}">
            <td class="border-t px-6 py-6">
              <select-input
                v-model="value.type"
                class="w-full"
                @change="handleType(index)"
              >
                <option value="text">Texto</option>
                <option value="number">Numérico</option>
                <option value="date">Data</option>
              </select-input>
            </td>

            <td class="border-t px-6 py-6">
              <text-input
                v-model="value.start_date"
                type="date"
                class="w-full"
              />
            </td>

            <td class="border-t px-6 py-6">
              <text-input
                v-model="value.end_date"
                type="date"
                class="w-full"
              />
            </td>

            <td class="border-t px-6 py-6 flex">
              <text-input
                v-model="value.value"
                :type="value.type"
                class="w-full"
              />

              <span class="my-auto ml-1 mr-auto flex-shrink-0 cursor-pointer" @click="delValue($event, index)">
                <icon name="trash" class="w-4 h-4 fill-red-600" />
              </span>
            </td>
          </tr>

          <tr>
            <td class="border-t px-6 py-6 cursor-pointer" colspan="4" @click="addValue()">
              <div class="flex">
                <icon name="plus" class="w-4 h-4 fill-green-600" />
                <span class="ml-2 mr-auto my-auto">
                  Adicionar
                </span>
              </div>
            </td>
          </tr>
        </table>

        <div class="px-8 py-4 bg-gray-50 border-t border-gray-100 flex justify-end items-center">
          <loading-button :loading="form.processing" class="btn-indigo ml-auto" type="submit">Atualizar Indicador</loading-button>
          <button v-if="!indicator.deleted_at" class="text-red-600 ml-3 hover:underline" tabindex="-1" type="button" @click="destroy">Deletar Indicador</button>
        </div>
      </form>
    </div>
  </div>
</template>

<script>
import Layout from '@/Shared/Layout'
import Icon from '@/Shared/Icon'
import LoadingButton from '@/Shared/LoadingButton'
import SelectInput from '@/Shared/SelectInput'
import TextInput from '@/Shared/TextInput'
import axios from 'axios'

export default {
  metaInfo() {
    return { title: this.form.name }
  },
  components: {
    Icon,
    LoadingButton,
    SelectInput,
    TextInput,
  },
  layout: Layout,
  remember: 'form',
  props: {
    organizations: Array,
    roles:         Array,

    states: Array,
    cities: Array,

    indicator: Object,
    values:    Array,
  },
  data() {
    return {
      form: this.$inertia.form({
        _method: 'put',
        organization: this.indicator.organization_id,
        role: this.indicator.role_id,
        label: this.indicator.label,
        active: this.indicator.active,
        state: this.indicator.state_id,
        city: this.indicator.city_id,
        values: this.values,
      }),
      userCities: this.cities,
    }
  },
  methods: {
    update() {
      this.form.post(this.route('indicators.update', this.indicator.id).url())
    },
    destroy() {
      if(confirm('Tem certeza de que deseja excluir este indicator?'))
      {
        this.$inertia.delete(this.route('indicators.destroy', this.indicator.id))
      }
    },
    handleType(index) {
      this.form.values[index].value = null
    },
    addValue() {
      this.form.values.push({
        type: 'text',
        start_date: null,
        end_date: null,
        value: null,
      })
    },
    delValue(event, index) {
      const target = (() => {
        const target = event.target

        if(target.tagName === 'path')
        {
          return target.parentElement.parentElement.parentElement.parentElement
        }

        if(target.tagName === 'svg')
        {
          return target.parentElement.parentElement.parentElement
        }

        if(target.tagName === 'span')
        {
          return target.parentElement.parentElement
        }

        return target
      })()

      this.form.values[index]['hidden'] = true
      this.form.values[index]['del'] = true

      target.classList.add('hidden')
    },
    getCities() {
      axios.get(this.route('cities'), {
        params: {
          stateId: this.form.state,
        },
      }).then((response) => {
        this.form.city = null

        this.userCities = response.data
      })
    },
  },
}
</script>

